import { TextField } from '@mui/material';
import classNames from 'classnames';
import { walletSelector } from 'modules/payments/store/wallet/selectors';
import React, { FC, FocusEventHandler, forwardRef, useMemo } from 'react';
import { default as NumberFormat, InputAttributes, NumberFormatProps } from 'react-number-format';
import { useSelector } from 'react-redux';
import { convertCryptoToUsd, CRYPTO_LABEL, formatFiat } from 'shared/utils';
import { useStyles } from '../NumberInput/NumberInput.styles';

type CurrencyFormatProps = NumberFormatProps<InputAttributes> & {
  onChange: (value: number) => void;
  onBlur: FocusEventHandler<HTMLInputElement>;
  showUsdEquivalent?: boolean;
};

const CurrencyFormat: FC<CurrencyFormatProps> = forwardRef(
  ({ onChange, onBlur, suffix, showUsdEquivalent = false, ...rest }, ref) => {
    const exchangeRate = useSelector(walletSelector.exchangeRate);

    const defaultSuffix = useMemo(() => {
      const inputValue = rest.value;

      let result = ` ${CRYPTO_LABEL}`;

      const floatValue = Number(inputValue);
      if (showUsdEquivalent && !Number.isNaN(floatValue)) {
        const equivalent = formatFiat(convertCryptoToUsd(floatValue, exchangeRate));
        result += ` (${equivalent})`;
      }

      return result;
    }, [exchangeRate, rest.value, showUsdEquivalent]);

    return (
      <NumberFormat
        decimalScale={2}
        allowNegative={false}
        fixedDecimalScale
        getInputRef={ref}
        thousandSeparator
        isNumericString
        suffix={suffix ?? defaultSuffix}
        {...rest}
        onValueChange={(value) => onChange(value.floatValue ?? 0)}
        onBlur={onBlur}
      />
    );
  }
);

export type CurrencyInputProps = NumberFormatProps & {
  error?: boolean;
  showUsdEquivalent?: boolean;
};

export const CurrencyInput: FC<CurrencyInputProps> = forwardRef((props, ref) => {
  const { classes } = useStyles();

  return (
    <TextField
      size="small"
      className={classNames(classes.root, { [classes.error]: props.error })}
      InputProps={{
        inputComponent: CurrencyFormat as never,
      }}
      disabled={props.disabled}
      inputProps={{ ...props, ref }}
      inputRef={ref}
    />
  );
});
